/**
 * The kart's constants, as a value rather than as module scope.
 *
 * Every number here was a `const` in `kart.ts`, and most of them still carry
 * the comment that argued for their value. Lifting them out does not make them
 * arbitrary — it makes them *addressable*, so `/tune` can put a slider on one
 * and someone can feel what it does in a second instead of editing, rebuilding
 * and re-recording.
 *
 * ## The rule that keeps this honest
 *
 * A tuned session must be able to say it was tuned. This project has been
 * burned once already by a recording that could not explain its own shape — see
 * `Recording.modeChanges` in `core/types.ts`, and the investigation it cost to
 * establish that nothing was broken. So `isDefault` exists, the diagnostics
 * trail records any departure from the defaults, and the recording carries the
 * tuning in its metadata. A number nobody can reconstruct is worse than a
 * number nobody can change.
 *
 * No DOM here, and no UI vocabulary beyond labels: `/tune` builds its controls
 * from `TUNABLES` so adding a slider is a data edit, exactly like adding a
 * theme.
 */

export interface KartTuning {
  /** Distance units per second at full speed. */
  baseSpeed: number;
  /** Most speed sustained suppression can take. Deliberately less than braking. */
  maxSuppressionDrag: number;
  /** Hardest the brake can bite. Never a full stop — a parked kart opts out. */
  maxBrake: number;
  /** Speed penalty for having both wheels off the track. */
  offroadDrag: number;
  /** How fast full lock moves you across the track, in track-widths per second. */
  lateralPerS: number;
  /** How fast the wheel returns to centre once the thumb lifts, per second. */
  centringPerS: number;
  /** Drag distance for full lock, as a fraction of the surface width. */
  fullLockFraction: number;
  /** Drag-back distance for maximum braking, as a fraction of surface height. */
  fullBrakeFraction: number;
  /** How long a full-power jump lasts, ms. */
  jumpMs: number;
  /** Most of the jump sustained suppression can take. Never all of it. */
  maxSuppressionAirLoss: number;
  /** Distance units between obstacles. */
  obstacleSpacing: number;
  /** Half-width of a partial obstacle, in track-widths. */
  obstacleHalfWidth: number;
  /** How long a hit keeps biting, ms. */
  stunMs: number;
  /** Speed multiplier while stunned. */
  stunDrag: number;
  /** Extra speed a full rhythm meter buys, as a fraction of base. */
  turboGain: number;
}

/**
 * The values the game shipped with, and the ones every test asserts against.
 *
 * Changing a number here changes the game. Changing one in `/tune` changes a
 * session, says so, and is forgotten on reset.
 */
export const DEFAULT_TUNING: KartTuning = {
  baseSpeed: 34,
  maxSuppressionDrag: 0.55,
  maxBrake: 0.75,
  offroadDrag: 0.45,
  lateralPerS: 0.85,
  centringPerS: 5,
  fullLockFraction: 0.22,
  fullBrakeFraction: 0.2,
  jumpMs: 620,
  maxSuppressionAirLoss: 0.55,
  obstacleSpacing: 115,
  obstacleHalfWidth: 0.34,
  stunMs: 900,
  stunDrag: 0.5,
  turboGain: 0.5,
};

export interface Tunable {
  key: keyof KartTuning;
  label: string;
  /** What moving it actually changes, in one line. Shown under the slider. */
  note: string;
  min: number;
  max: number;
  step: number;
  unit?: string;
}

/**
 * What `/tune` puts a slider on, and the range it is allowed to explore.
 *
 * Ranges are bounded rather than open. Two of these have a floor that is not
 * cosmetic: `maxSuppressionDrag` and `maxSuppressionAirLoss` must stay below 1,
 * because a kart that suppression can bring to a complete stop lets the player
 * be *stuck* rather than slowed — and someone who has stopped moving has
 * stopped receiving the lesson. That argument is in `kart.ts` and the sliders
 * inherit it rather than allowing it to be tuned away by accident.
 */
export const TUNABLES: readonly Tunable[] = [
  {
    key: "baseSpeed",
    label: "Velocidade base",
    note: "Distância por segundo a 100%. Define quanto se percorre em 90s.",
    min: 10,
    max: 80,
    step: 1,
  },
  {
    key: "maxSuppressionDrag",
    label: "Arrasto da supressão",
    note: "Quanto da velocidade uma piscada segurada tira. Nunca chega a parar.",
    min: 0,
    max: 0.9,
    step: 0.05,
  },
  {
    key: "maxSuppressionAirLoss",
    label: "Perda de altura",
    note: "Quanto do pulo a supressão tira. O kart fica pesado, não preso.",
    min: 0,
    max: 0.9,
    step: 0.05,
  },
  {
    key: "jumpMs",
    label: "Duração do pulo",
    note: "Quanto tempo no ar a potência máxima compra.",
    min: 250,
    max: 1200,
    step: 20,
    unit: "ms",
  },
  {
    key: "turboGain",
    label: "Turbo do ritmo",
    note: "Velocidade extra que a barra cheia compra. É a única recompensa do jogo.",
    min: 0,
    max: 1.5,
    step: 0.05,
  },
  {
    key: "maxBrake",
    label: "Força do freio",
    note: "Quanto puxar para trás tira. Sem ré, sem parada total.",
    min: 0,
    max: 0.95,
    step: 0.05,
  },
  {
    key: "lateralPerS",
    label: "Resposta da direção",
    note: "Larguras de pista por segundo com o volante no batente.",
    min: 0.2,
    max: 2.5,
    step: 0.05,
  },
  {
    key: "centringPerS",
    label: "Retorno ao centro",
    note: "Quão rápido o volante volta quando o polegar sai.",
    min: 0.5,
    max: 15,
    step: 0.5,
  },
  {
    key: "fullLockFraction",
    label: "Curso do polegar",
    note: "Fração da largura da tela até o batente. Menor = mais sensível.",
    min: 0.05,
    max: 0.6,
    step: 0.01,
  },
  {
    key: "offroadDrag",
    label: "Penalidade fora da pista",
    note: "Quanto sair da pista custa.",
    min: 0,
    max: 0.9,
    step: 0.05,
  },
  {
    key: "obstacleSpacing",
    label: "Espaço entre obstáculos",
    note: "Distância entre um e o próximo. Menor = mais frequente.",
    min: 50,
    max: 400,
    step: 5,
  },
  {
    key: "stunMs",
    label: "Duração da batida",
    note: "Quanto tempo uma batida continua doendo.",
    min: 100,
    max: 3000,
    step: 50,
    unit: "ms",
  },
  {
    key: "stunDrag",
    label: "Velocidade na batida",
    note: "Multiplicador enquanto atordoado. Menor dói mais.",
    min: 0.05,
    max: 1,
    step: 0.05,
  },
];

export function isDefault(tuning: KartTuning): boolean {
  return (Object.keys(DEFAULT_TUNING) as (keyof KartTuning)[]).every(
    (key) => tuning[key] === DEFAULT_TUNING[key],
  );
}

/** Only the values that differ from the defaults, for a log line worth reading. */
export function describeTuning(tuning: KartTuning): string {
  const changed = (Object.keys(DEFAULT_TUNING) as (keyof KartTuning)[])
    .filter((key) => tuning[key] !== DEFAULT_TUNING[key])
    .map((key) => `${key}=${tuning[key]}`);
  return changed.length === 0 ? "padrão" : changed.join(" ");
}

/**
 * Merges stored or shared values over the defaults, discarding anything unsafe.
 *
 * Deliberately forgiving about missing keys and unforgiving about bad ones: a
 * tuning saved before a knob existed must still load, and a NaN reaching
 * `baseSpeed` would make the kart's distance NaN for the rest of the session
 * with no visible cause.
 */
export function coerceTuning(input: unknown): KartTuning {
  const tuning = { ...DEFAULT_TUNING };
  if (typeof input !== "object" || input === null) return tuning;

  const limits = new Map(TUNABLES.map((t) => [t.key, t]));
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (!(key in tuning) || typeof value !== "number" || !Number.isFinite(value)) continue;
    const limit = limits.get(key as keyof KartTuning);
    // Clamped to the slider's own range. A value outside it could only have
    // come from a hand-edited payload, and the ranges encode real constraints.
    tuning[key as keyof KartTuning] = limit
      ? Math.min(limit.max, Math.max(limit.min, value))
      : value;
  }
  return tuning;
}
